import { PermissionChip } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsTransferStatus/PermissionChip';
import { TransferHeader } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsTransferStatus/TransferHeader';
import { TransferLeg } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsTransferStatus/TransferLeg';
import { TONE } from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { selectPermissions, useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '~/lib/utils';

/**
 * CallsTransferStatus - Warm transfer state, leg by leg
 *
 * Shows which leg is the consultation and which is the original, because
 * that relationship exists only in `warmTransferContext` and is invisible
 * from the call list alone.
 */
export const CallsTransferStatus: React.FC = () => {
  const {
    warmTransferContext,
    isTransferActive,
    isCompletingTransfer,
    activeCalls,
    canTransfer,
    canMerge,
  } = useSoftphoneStore(
    useShallow((state) => {
      const permissions = selectPermissions(state);

      return {
        warmTransferContext: state.warmTransferContext,
        isTransferActive: state.isTransferActive,
        isCompletingTransfer: state.isCompletingTransfer,
        activeCalls: state.activeCalls,
        canTransfer: permissions.canTransfer,
        canMerge: permissions.canMerge,
      };
    })
  );

  if (!isTransferActive && !warmTransferContext) {
    return null;
  }

  const legs = [
    {
      role: warmTransferContext?.destination
        ? 'Original leg · held'
        : 'Original leg',
      id: warmTransferContext?.originalCallId ?? null,
      missing: 'missing from the transfer context',
    },
    {
      role: warmTransferContext?.destination
        ? `Consultation leg → ${warmTransferContext.destination}`
        : 'Consultation leg',
      id: warmTransferContext?.consultationCallId ?? null,
      missing: 'not yet dialled',
    },
  ];

  return (
    <div className={TONE.warn.card}>
      <TransferHeader
        isWarm={Boolean(warmTransferContext)}
        state={
          isCompletingTransfer
            ? 'completing'
            : (warmTransferContext?.state ?? 'waiting')
        }
      />

      <div className="p-3">
        {warmTransferContext && (
          <div className="flex items-stretch gap-2.5">
            {/* One spine for both legs: they are two halves of one operation. */}
            <span className="w-0.5 flex-shrink-0 rounded-sm bg-gradient-to-b from-violet-400 to-sky-400" />
            <div className="flex min-w-0 flex-1 flex-col gap-2.5">
              {legs.map((leg) => (
                <TransferLeg
                  key={leg.role}
                  {...leg}
                  isVoicemail={Boolean(
                    leg.id &&
                      activeCalls.get(leg.id)?.answeredByVoicemail === true
                  )}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className={cn(
            'flex gap-1.5 border-t border-zinc-800 pt-2.5',
            warmTransferContext && 'mt-3'
          )}
        >
          <PermissionChip label="canTransfer" allowed={canTransfer} />
          <PermissionChip label="canMerge" allowed={canMerge} />
        </div>

        {!canMerge && (
          <p className="mt-2 text-[11px] leading-[1.5] text-zinc-400">
            A call inside a conference cannot be transferred, and{' '}
            <span className="font-sp-mono">canMerge</span> stays false until a
            second established leg exists.
          </p>
        )}
      </div>
    </div>
  );
};
